import React, { useEffect } from "react"
import logo from "../../assets/images/Logo2.png"
import { useNavigate } from "react-router-dom"
import Menu from "../../components/Menu/Menu"
import useLogin from "../../hooks/Components/useLogin"
import Button from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { faArrowRight, faKey, faUser } from "@fortawesome/free-solid-svg-icons"
import { faGoogle } from "@fortawesome/free-brands-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PATH_MAP } from "../../configuration/PathMap"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useConfirmation } from "../../context/useConfirmationContext"
import { useMessage } from "../../context/useMessageContext"
import { useTranslation } from "react-i18next"
import { useResetPassword } from "../../hooks/Components/useResetPassword"
import { useGoogleLogin } from "../../hooks/Components/useGoogleLogin"

const LoginPage: React.FC = () => {
  const { t } = useTranslation("login")

  const navigate = useNavigate()

  const { setNewConfirmation } = useConfirmation()

  const { HandleRecoverPassword } = useResetPassword()

  const { setMessage } = useMessage()

  const { email, password, setEmail, setPassword, handleLogin } = useLogin()

  useEffect(() => {
    const storedEmail = localStorage.getItem(API_ROUTES.EMAIL_KEY) || ""
    setEmail(storedEmail.replace(/"/g, ""))
  }, [])

  const handleSuccess = () => {
    navigate(PATH_MAP.COMMANDS_PAGE)
  }

  const { googleButtonRef, isGoogleLoginAvailable } = useGoogleLogin({
    onSuccess: handleSuccess,
  })

  return (
    <Animate type={AnimationTypes.fadeIn}>
      <div className="min-h-screen flex flex-col items-center justify-center relative bg-white overflow-hidden">
        <Menu />

        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
          <img src={logo} alt="Logo do Mestre da Comanda" className="sm:mt-24 mt-16 w-40 md:w-80 lg:w-80 z-10" />
        </Animate>

        <div className="h-6" />

        <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
          <h1 className="text-gray-500 text-lg md:text-xl font-semibold text-center drop-shadow-md z-10">
            {t("title")}
          </h1>
        </Animate>

        <div className="h-2" />

        <form
          onSubmit={(e) => handleLogin(e, handleSuccess)}
          className="z-10 sm:w-2/3 md:w-1/2 xl:w-1/4 bg-white/90 backdrop-blur-md p-6 rounded-2xl "
        >
          <InputField
            required
            type="text"
            icon={faUser}
            value={email}
            animationDuration="2s"
            placeholder={t("input_user_placeholder")}
            onChange={(e) => setEmail(e.target.value)}
            descriptionKey="interactionDescriptions:pages.loginPage.loginPage.input.userPlaceholder"
          />

          <div className="h-3" />

          <InputField
            required
            type="password"
            icon={faKey}
            value={password}
            animationDuration="2.5s"
            placeholder={t("input_password_placeholder")}
            onChange={(e) => setPassword(e.target.value)}
            descriptionKey="interactionDescriptions:pages.loginPage.loginPage.input.passwordPlaceholder"
          />

          <div className="h-4" />

          <Button
            typeButton="submit"
            type="primary"
            bgColor="#F54927"
            size="large"
            animationDuration="3s"
            label={t("button_sing_in")}
            descriptionKey="interactionDescriptions:pages.loginPage.loginPage.button.singIn"
          />

          {isGoogleLoginAvailable && (
            <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
              <div className="space-y-3 pt-4 p-2">
                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="text-center text-xs font-semibold text-slate-500">{t("google_login_divider")}</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                <div className="group relative mx-auto h-11 w-full max-w-[400px] overflow-hidden rounded-xl shadow-md transition hover:-translate-y-0.5 hover:shadow-lg">
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center gap-3 bg-gradient-to-r from-red-600 to-orange-500 px-2 py-4 text-white">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-red-500 shadow-sm">
                      <FontAwesomeIcon icon={faGoogle} />
                    </span>

                    <span className="flex-1 text-left text-sm font-black">{t("google_login_button")}</span>

                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>

                  <div
                    className="absolute inset-0 z-20 overflow-hidden opacity-[0.01] [&>div]:h-full [&>div]:w-full [&_iframe]:h-full [&_iframe]:w-full"
                    ref={googleButtonRef}
                  />
                </div>
              </div>
            </Animate>
          )}

          {email.length > 0 && (
            <>
              <div className="h-3" />

              <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
                <p
                  className="text-center text-sm font-semibold text-red-700 hover:text-orange-600 cursor-pointer transition-colors"
                  onClick={() =>
                    setNewConfirmation(
                      t("label_title_recover_account"),
                      t("label_description_recover_account"),
                      async () => {
                        await HandleRecoverPassword(email, () =>
                          setMessage(t("label_success_recover_account"), t("label_confirmation_recover_account")),
                        )
                      },
                    )
                  }
                  data-interaction-description-key="interactionDescriptions:pages.loginPage.loginPage.button.titleRecoverAccount"
                >
                  {t("label_forget_password")}
                </p>
              </Animate>
            </>
          )}
        </form>
      </div>
    </Animate>
  )
}

export default LoginPage
